import React from "react";
import { Link } from "react-router-dom";
import { db } from "../lib/instant";
import { useAuth } from "../context/AuthContext";
import { Package, Clock, CheckCircle, Truck, XCircle, AlertCircle, FileText } from "lucide-react";

const STATUS_CONFIG = {
  "Pending Payment": { color: "#f57c00", bg: "#fff3e0", icon: <Clock size={14}/> },
  "Payment Received": { color: "#1976d2", bg: "#e3f2fd", icon: <CheckCircle size={14}/> },
  "Processing": { color: "#7b1fa2", bg: "#f3e5f5", icon: <AlertCircle size={14}/> },
  "Shipped": { color: "#0288d1", bg: "#e1f5fe", icon: <Truck size={14}/> },
  "Delivered": { color: "#388e3c", bg: "#e8f5e9", icon: <CheckCircle size={14}/> },
  "Cancelled": { color: "#d32f2f", bg: "#ffebee", icon: <XCircle size={14}/> },
};

export default function Orders({ theme }) {
  const primaryColor = theme?.primaryColor || "#6b2118";
  const accentColor = theme?.accentColor || "#c9a84c";
  const { user } = useAuth();

  const { data, isLoading } = db.useQuery(
    user ? { orders: { $: { where: { userId: user.id }, order: { serverCreatedAt: "desc" } } } } : {}
  );

  const orders = data?.orders || [];

  if (!user) return (
    <div style={{ paddingTop: 100, textAlign: "center", minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}>
      <h2 style={{ color: "#2c1810", fontFamily: "Georgia, serif" }}>Please log in to view orders</h2>
      <Link to="/login" style={{ background: primaryColor, color: "#fff", padding: "12px 32px", borderRadius: 40, textDecoration: "none", fontWeight: 700 }}>Login</Link>
    </div>
  );

  return (
    <div style={{ paddingTop: 64, minHeight: "100vh", background: "#fdf6e3" }}>
      <div style={{ background: primaryColor, padding: "48px 5vw 36px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ fontSize: "0.72rem", letterSpacing: 4, textTransform: "uppercase", color: accentColor, fontWeight: 700, marginBottom: 8 }}>Account</div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.8rem, 4vw, 3rem)", color: "#fff" }}>My Orders</h1>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 5vw" }}>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>Loading orders...</div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
            <Package size={64} color="#ddd" />
            <h2 style={{ color: "#999", fontFamily: "Georgia, serif" }}>No orders yet</h2>
            <Link to="/shop" style={{ background: primaryColor, color: "#fff", padding: "12px 32px", borderRadius: 40, textDecoration: "none", fontWeight: 700 }}>Start Shopping</Link>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {orders.map(order => {
              const items = (() => { try { return JSON.parse(order.items || "[]"); } catch { return []; } })();
              const statusConf = STATUS_CONFIG[order.status] || STATUS_CONFIG["Pending Payment"];
              return (
                <div key={order.id} style={{ background: "#fff", borderRadius: 20, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>
                  {/* Header */}
                  <div style={{ background: "#f9f5f0", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, borderBottom: "1px solid #f0e8d0" }}>
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "#999", marginBottom: 2 }}>Order ID</div>
                      <div style={{ fontWeight: 700, color: "#2c1810", fontFamily: "monospace", fontSize: "0.9rem" }}>#{order.id?.slice(0, 8).toUpperCase()}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "0.75rem", color: "#999", marginBottom: 2 }}>Date</div>
                      <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{new Date(order.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "0.75rem", color: "#999", marginBottom: 2 }}>Total</div>
                      <div style={{ fontWeight: 800, color: primaryColor, fontSize: "1rem" }}>₹{Number(order.total || 0).toFixed(2)}</div>
                    </div>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: statusConf.bg, color: statusConf.color,
                      padding: "6px 14px", borderRadius: 20, fontSize: "0.8rem", fontWeight: 700,
                    }}>{statusConf.icon} {order.status}</div>
                  </div>

                  {/* Items */}
                  <div style={{ padding: "20px 24px" }}>
                    {items.map((item, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < items.length - 1 ? "1px solid #f5f5f5" : "none" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: "0.92rem", color: "#2c1810" }}>{item.name}</div>
                          <div style={{ fontSize: "0.78rem", color: "#999" }}>Qty: {item.quantity} × ₹{item.price}</div>
                        </div>
                        <div style={{ fontWeight: 700, color: "#333" }}>₹{(item.price * item.quantity).toFixed(2)}</div>
                      </div>
                    ))}

                    {/* Delivery address */}
                    <div style={{ marginTop: 16, padding: "14px 16px", background: "#f9f5f0", borderRadius: 12, fontSize: "0.82rem", color: "#666" }}>
                      <strong style={{ color: "#2c1810" }}>📍 Delivering to:</strong> {order.address}
                    </div>

                    {/* Payment status note */}
                    {order.status === "Pending Payment" && (
                      <div style={{ marginTop: 12, padding: "12px 16px", background: "#fff8e1", borderRadius: 12, fontSize: "0.82rem", color: "#795548", border: "1px solid #ffe082" }}>
                        ⏳ Awaiting payment verification. We'll confirm your order shortly.
                      </div>
                    )}
                    {order.status === "Payment Received" && (
                      <div style={{ marginTop: 12, padding: "12px 16px", background: "#e8f5e9", borderRadius: 12, fontSize: "0.82rem", color: "#388e3c" }}>
                        ✅ Payment received! Your order is being prepared.
                      </div>
                    )}
                    <div style={{ marginTop: 14 }}>
                      <Link to={`/invoice/${order.id}`} style={{ display:"inline-flex", alignItems:"center", gap:6, color: primaryColor, textDecoration:"none", fontSize:"0.82rem", fontWeight:700, border:`1px solid ${primaryColor}30`, padding:"7px 14px", borderRadius:8, background:`${primaryColor}08` }}>
                        <FileText size={13}/> View Invoice
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
